import { createContractCenterRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  AnalyticsContractsMeta,
  AnalyticsContractsOverview,
  AnalyticsDistributionItem,
  AnalyticsExpiringContract,
  AnalyticsOverviewMetrics,
  AnalyticsQuery,
  AnalyticsSettlementDirectionOverview,
  AnalyticsSettlementOverview,
  AnalyticsTrendItem,
  ContractAnalysisQuery,
  ContractAnalysisResult,
  ContractAmountBucketItem,
  ContractTypeAnalysisItem,
  CounterpartyAnalysisItem,
  OwnerAnalysisItem,
  PerformanceAnalysis,
  PerformanceAnalysisQuery,
  PerformanceExpiringContractItem,
  PerformanceMetrics,
  PerformanceOverdueContractItem,
  PerformancePage,
  SettlementAnalysis,
  SettlementAnalysisDirection,
  SettlementAnalysisTrendItem,
  SettlementFinanceSyncSummary,
  SettlementOverduePlanItem,
} from './types/analytics';

const request = createContractCenterRequest();

const emptyAmount = '0.00';

const emptySettlement = (): AnalyticsSettlementDirectionOverview => ({
  plannedAmount: emptyAmount,
  settledAmount: emptyAmount,
  outstandingAmount: emptyAmount,
});

const toCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const toDays = (value: unknown) => {
  const days = Number(value);
  return Number.isFinite(days) ? days : 0;
};

const toAmount = (value: unknown) => {
  if (typeof value === 'string' && value.trim()) return value;
  return emptyAmount;
};

const normalizeTrend = (items: AnalyticsTrendItem[] = []): AnalyticsTrendItem[] =>
  items.map((item) => ({
    period: item.period || '',
    count: toCount(item.count),
    amount: toAmount(item.amount),
  }));

const normalizeDistribution = (
  items: AnalyticsDistributionItem[] = [],
): AnalyticsDistributionItem[] =>
  items.map((item) => ({
    key: item.key || '',
    name: item.name || item.key || '',
    count: toCount(item.count),
    amount: toAmount(item.amount),
  }));

const normalizeExpiring = (
  items: AnalyticsExpiringContract[] = [],
): AnalyticsExpiringContract[] =>
  items.map((item) => ({
    contractUuid: item.contractUuid || '',
    contractNo: item.contractNo || '',
    contractName: item.contractName || '',
    contractAmount: toAmount(item.contractAmount),
    expirationDate: item.expirationDate || '',
    remainingDays: toDays(item.remainingDays),
    ownerMemberUuid: item.ownerMemberUuid || '',
    ownerName: item.ownerName || '',
    counterpartyName: item.counterpartyName || '',
    contractTypeName: item.contractTypeName || '',
  }));

const normalizeSettlementSide = (
  value?: AnalyticsSettlementDirectionOverview,
): AnalyticsSettlementDirectionOverview => ({
  plannedAmount: toAmount(value?.plannedAmount),
  settledAmount: toAmount(value?.settledAmount),
  outstandingAmount: toAmount(value?.outstandingAmount),
});

export const normalizeOverview = (
  value: AnalyticsContractsOverview,
): AnalyticsContractsOverview => {
  const metrics = value?.metrics || ({} as AnalyticsOverviewMetrics);
  const settlement =
    value?.settlementOverview || ({} as AnalyticsSettlementOverview);
  return {
    currency: value?.currency || null,
    requiresCurrencySelection: value?.requiresCurrencySelection === true,
    availableCurrencies: Array.isArray(value?.availableCurrencies)
      ? value.availableCurrencies.filter(Boolean)
      : [],
    metrics: {
      contractCount: toCount(metrics.contractCount),
      contractAmount: toAmount(metrics.contractAmount),
      activeContractAmount: toAmount(metrics.activeContractAmount),
      currentMonthNewAmount: toAmount(metrics.currentMonthNewAmount),
      pendingApprovalCount: toCount(metrics.pendingApprovalCount),
      expiringSoonCount: toCount(metrics.expiringSoonCount),
      overdueCount: toCount(metrics.overdueCount),
      settlementExceptionCount: toCount(metrics.settlementExceptionCount),
    },
    trend: normalizeTrend(value?.trend),
    statusDistribution: normalizeDistribution(value?.statusDistribution),
    typeDistribution: normalizeDistribution(value?.typeDistribution),
    expiringContracts: normalizeExpiring(value?.expiringContracts),
    settlementOverview: {
      income: normalizeSettlementSide(settlement.income) || emptySettlement(),
      expense: normalizeSettlementSide(settlement.expense) || emptySettlement(),
    },
  };
};

export const getAnalyticsMeta = (
  options?: ApiRequestOptions,
): Promise<AnalyticsContractsMeta> =>
  request<AnalyticsContractsMeta>('/bff/analytics/contracts/meta', {
    method: 'GET',
    ...(options || {}),
  });

export const getAnalyticsOverview = async (
  params: AnalyticsQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<AnalyticsContractsOverview>(
    '/bff/analytics/contracts/overview',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return normalizeOverview(value);
};

const normalizeTypeAnalysis = (
  items: ContractTypeAnalysisItem[] = [],
): ContractTypeAnalysisItem[] =>
  items.map((item) => ({
    key: item.key || '',
    name: item.name || item.key || '',
    count: toCount(item.count),
    amount: toAmount(item.amount),
    averageAmount: toAmount(item.averageAmount),
    amountRatio: Number.isFinite(Number(item.amountRatio))
      ? Number(item.amountRatio)
      : 0,
  }));

const normalizeBuckets = (
  items: ContractAmountBucketItem[] = [],
): ContractAmountBucketItem[] =>
  items.map((item) => ({
    key: item.key || '',
    name: item.name || item.key || '',
    count: toCount(item.count),
    amount: toAmount(item.amount),
    countRatio: Number.isFinite(Number(item.countRatio))
      ? Number(item.countRatio)
      : 0,
  }));

const normalizeCounterparties = (
  items: CounterpartyAnalysisItem[] = [],
): CounterpartyAnalysisItem[] =>
  items.map((item) => ({
    counterpartyPartyUuid: item.counterpartyPartyUuid || '',
    counterpartyName: item.counterpartyName || item.counterpartyPartyUuid || '',
    contractCount: toCount(item.contractCount),
    contractAmount: toAmount(item.contractAmount),
    averageAmount: toAmount(item.averageAmount),
    amountRatio: Number.isFinite(Number(item.amountRatio))
      ? Number(item.amountRatio)
      : 0,
  }));

const normalizeOwners = (items: OwnerAnalysisItem[] = []): OwnerAnalysisItem[] =>
  items.map((item) => ({
    ownerMemberUuid: item.ownerMemberUuid || '',
    ownerName: item.ownerName || item.ownerMemberUuid || '',
    contractCount: toCount(item.contractCount),
    contractAmount: toAmount(item.contractAmount),
    averageAmount: toAmount(item.averageAmount),
  }));

export const normalizeContractAnalysis = (
  value: ContractAnalysisResult,
): ContractAnalysisResult => {
  const summary = value?.periodSummary || {};
  const normalizeSummary = (item?: { count: number; amount: string }) => ({
    count: toCount(item?.count),
    amount: toAmount(item?.amount),
  });
  return {
    currency: value?.currency || null,
    trend: normalizeTrend(value?.trend),
    signedTrend: normalizeTrend(value?.signedTrend),
    effectiveTrend: normalizeTrend(value?.effectiveTrend),
    expirationTrend: normalizeTrend(value?.expirationTrend),
    periodSummary: {
      created: normalizeSummary(summary.created),
      signed: normalizeSummary(summary.signed),
      effective: normalizeSummary(summary.effective),
      expired: normalizeSummary(summary.expired),
    },
    typeAnalysis: normalizeTypeAnalysis(value?.typeAnalysis),
    amountBuckets: normalizeBuckets(value?.amountBuckets),
    topCounterparties: normalizeCounterparties(value?.topCounterparties),
    topOwners: normalizeOwners(value?.topOwners),
  };
};

export const getAnalyticsAnalysis = async (
  params: ContractAnalysisQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<ContractAnalysisResult>(
    '/bff/analytics/contracts/analysis',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return normalizeContractAnalysis(value);
};

const emptyPerformancePage = <T,>(): PerformancePage<T> => ({
  items: [],
  pageNumber: 1,
  pageSize: 20,
  total: 0,
});

const normalizePerformanceMetrics = (value?: PerformanceMetrics): PerformanceMetrics => ({
  performingCount: toCount(value?.performingCount),
  performingAmount: toAmount(value?.performingAmount),
  expiringSoonCount: toCount(value?.expiringSoonCount),
  overdueCount: toCount(value?.overdueCount),
  completedCount: toCount(value?.completedCount),
});

const normalizePerformanceExpiringItem = (
  item: PerformanceExpiringContractItem,
): PerformanceExpiringContractItem => ({
  contractUuid: item.contractUuid || '',
  contractNo: item.contractNo || '',
  contractName: item.contractName || '',
  contractTypeUuid: item.contractTypeUuid || '',
  contractTypeName: item.contractTypeName || '',
  contractNature: item.contractNature || '',
  contractAmount: item.contractAmount ?? null,
  ownerMemberUuid: item.ownerMemberUuid || '',
  ownerName: item.ownerName || '',
  counterpartyPartyUuid: item.counterpartyPartyUuid,
  counterpartyName: item.counterpartyName || '',
  actualEffectiveDate: item.actualEffectiveDate,
  expirationDate: item.expirationDate || '',
  remainingDays: toDays(item.remainingDays),
});

const normalizePerformanceOverdueItem = (
  item: PerformanceOverdueContractItem,
): PerformanceOverdueContractItem => ({
  contractUuid: item.contractUuid || '',
  contractNo: item.contractNo || '',
  contractName: item.contractName || '',
  contractTypeName: item.contractTypeName || '',
  contractNature: item.contractNature || '',
  contractAmount: item.contractAmount ?? null,
  ownerMemberUuid: item.ownerMemberUuid || '',
  ownerName: item.ownerName || '',
  counterpartyName: item.counterpartyName || '',
  actualEffectiveDate: item.actualEffectiveDate,
  expirationDate: item.expirationDate || '',
  overdueDays: toDays(item.overdueDays),
});

const normalizePerformancePage = <T,>(
  value: PerformancePage<T> | undefined,
  mapItem: (item: T) => T,
): PerformancePage<T> => {
  const page = value || emptyPerformancePage<T>();
  return {
    items: (page.items || []).map(mapItem),
    pageNumber: toCount(page.pageNumber) || 1,
    pageSize: toCount(page.pageSize) || 20,
    total: toCount(page.total),
  };
};

export const normalizePerformanceAnalysis = (
  value: PerformanceAnalysis,
): PerformanceAnalysis => ({
  metrics: normalizePerformanceMetrics(value?.metrics),
  expirationBuckets: normalizeDistribution(value?.expirationBuckets),
  expiringContracts: normalizePerformancePage(
    value?.expiringContracts,
    normalizePerformanceExpiringItem,
  ),
  overdueContracts: normalizePerformancePage(
    value?.overdueContracts,
    normalizePerformanceOverdueItem,
  ),
});

export const getAnalyticsPerformance = async (
  params: AnalyticsQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<PerformanceAnalysis>(
    '/bff/analytics/contracts/performance',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return normalizePerformanceAnalysis(value);
};

export const listAnalyticsPerformanceExpiring = async (
  params: PerformanceAnalysisQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<PerformancePage<PerformanceExpiringContractItem>>(
    '/bff/analytics/contracts/performance/expiring',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return normalizePerformancePage(value, normalizePerformanceExpiringItem);
};

export const listAnalyticsPerformanceOverdue = async (
  params: PerformanceAnalysisQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<PerformancePage<PerformanceOverdueContractItem>>(
    '/bff/analytics/contracts/performance/overdue',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return normalizePerformancePage(value, normalizePerformanceOverdueItem);
};

const emptySettlementDirection = (): SettlementAnalysisDirection => ({
  contractAmount: emptyAmount,
  plannedAmount: emptyAmount,
  actualAmount: emptyAmount,
  outstandingAmount: emptyAmount,
  overdueAmount: emptyAmount,
  progress: 0,
});

const normalizeSettlementDirection = (
  value?: SettlementAnalysisDirection,
): SettlementAnalysisDirection => ({
  contractAmount: toAmount(value?.contractAmount),
  plannedAmount: toAmount(value?.plannedAmount),
  actualAmount: toAmount(value?.actualAmount),
  outstandingAmount: toAmount(value?.outstandingAmount),
  overdueAmount: toAmount(value?.overdueAmount),
  progress: Number.isFinite(Number(value?.progress)) ? Number(value?.progress) : 0,
});

const normalizeSettlementTrend = (
  items: SettlementAnalysisTrendItem[] = [],
): SettlementAnalysisTrendItem[] =>
  items.map((item) => ({
    period: item.period || '',
    plannedReceivable: toAmount(item.plannedReceivable),
    actualReceived: toAmount(item.actualReceived),
    plannedPayable: toAmount(item.plannedPayable),
    actualPaid: toAmount(item.actualPaid),
  }));

const normalizeSettlementOverduePlans = (
  items: SettlementOverduePlanItem[] = [],
): SettlementOverduePlanItem[] =>
  items.map((item) => ({
    settlementPlanUuid: item.settlementPlanUuid || '',
    contractUuid: item.contractUuid || '',
    contractNo: item.contractNo || '',
    contractName: item.contractName || '',
    counterpartyName: item.counterpartyName || '',
    direction: item.direction === 'EXPENSE' ? 'EXPENSE' : 'INCOME',
    plannedAmount: toAmount(item.plannedAmount),
    actualAmount: toAmount(item.actualAmount),
    outstandingAmount: toAmount(item.outstandingAmount),
    plannedDate: item.plannedDate || '',
    overdueDays: toDays(item.overdueDays),
  }));

const normalizeSettlementSync = (
  value?: SettlementFinanceSyncSummary,
): SettlementFinanceSyncSummary => ({
  successCount: toCount(value?.successCount),
  unsyncedCount: toCount(value?.unsyncedCount),
  failedCount: toCount(value?.failedCount),
  lastSyncedAt: value?.lastSyncedAt || null,
});

export const normalizeSettlementAnalysis = (value?: SettlementAnalysis): SettlementAnalysis => ({
  currency: value?.currency || null,
  requiresCurrencySelection: value?.requiresCurrencySelection === true,
  availableCurrencies: Array.isArray(value?.availableCurrencies)
    ? value.availableCurrencies.filter(Boolean)
    : [],
  income: normalizeSettlementDirection(value?.income) || emptySettlementDirection(),
  expense: normalizeSettlementDirection(value?.expense) || emptySettlementDirection(),
  trend: normalizeSettlementTrend(value?.trend),
  overduePlans: normalizeSettlementOverduePlans(value?.overduePlans),
  sync: normalizeSettlementSync(value?.sync),
});

export const getAnalyticsSettlement = async (
  params: AnalyticsQuery = {},
  options?: ApiRequestOptions,
) => {
  const value = await request<SettlementAnalysis>('/bff/analytics/contracts/settlement', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizeSettlementAnalysis(value);
};
